import { useEffect, useState, type ReactNode } from "react"
import { client } from "@client/client"
import { AdminActivityAction, AdminActivityEntityType } from "@shared/admin-activities"
import { Modal, ModalActions, ModalButton } from "./modal"

interface ActivityItem {
  id: string
  action: string
  title: string
  body: string | null
  actorName: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value))
}

function metadataText(value: unknown) {
  return typeof value === "string" ? value : null
}

function communicationType(item: ActivityItem, metadata: Record<string, unknown>) {
  const storedType = metadataText(metadata.communicationType)
  if (storedType) return storedType
  return item.action.endsWith(".message_sent") ? "Email" : null
}

export function ActivityTimeline({
  entityType,
  entityId,
  title = "Activity",
  action,
  excludeActions = [],
  fallback,
  hideWhenEmpty = false,
  refreshKey = 0,
}: {
  entityType: AdminActivityEntityType
  entityId: string
  title?: string
  action?: AdminActivityAction
  excludeActions?: AdminActivityAction[]
  fallback?: ReactNode
  hideWhenEmpty?: boolean
  refreshKey?: number
}) {
  const [activities, setActivities] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCommunication, setSelectedCommunication] = useState<ActivityItem | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const response = await client.api.admin.activities.$get({
          query: { entityType, entityId, limit: "100", offset: "0" },
        })
        const body = await response.json()
        if (!cancelled && response.ok && "data" in body) {
          setActivities(body.data.items as ActivityItem[])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [entityId, entityType, refreshKey])

  const visibleActivities = activities.filter((item) => {
    if (action && item.action !== action) return false
    return !excludeActions.includes(item.action as AdminActivityAction)
  })
  if (loading) return null
  if (visibleActivities.length === 0 && hideWhenEmpty) return null
  if (visibleActivities.length === 0 && fallback) return <>{fallback}</>

  return (
    <div className="rounded-[10px] border border-gold-500 bg-white p-5">
      <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">{title}</h2>
      {visibleActivities.length > 0 ? (
        <div className="mt-4 space-y-4">
           {visibleActivities.map((item) => {
             const metadata = item.metadata ?? {}
             const items = Array.isArray(metadata.items) ? metadata.items.map(String) : []
             const respondBy = metadataText(metadata.respondBy)
             const sentTo = metadataText(metadata.sentTo)
             const note = metadataText(metadata.note)
             const message = metadataText(metadata.message)
             const subject = metadataText(metadata.subject) ?? item.body
             const type = communicationType(item, metadata)
             const canViewCommunication = Boolean(message && type)
             return (
               <div key={item.id} className="flex items-start gap-3">
                 <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-forest-700" />
                 <div className="min-w-0 flex-1">
                   <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                     <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                       <p className="text-sm font-medium text-ink">{item.title}</p>
                       <p className="text-sm text-ink/50">· {formatDate(item.createdAt)}</p>
                     </div>
                     {canViewCommunication && (
                       <button
                         type="button"
                         onClick={() => setSelectedCommunication(item)}
                         className="text-sm font-medium text-forest-700 underline decoration-gold-500 underline-offset-4 transition-colors hover:text-forest-900"
                       >
                         View
                       </button>
                     )}
                   </div>
                   {item.body && <p className="mt-1 text-sm text-ink/70">{item.body}</p>}
                   {item.actorName && <p className="mt-1 text-xs text-ink/50">By {item.actorName}</p>}
                  {(sentTo || respondBy || items.length > 0 || note) && (
                    <div className="mt-3 rounded-lg bg-cream-100 p-3 text-sm text-ink/70">
                      {sentTo && <p>Sent to: {sentTo}</p>}
                      {respondBy && <p>Respond by: {respondBy}</p>}
                      {items.length > 0 && <p>Requested: {items.join(", ")}</p>}
                      {note && <p className="mt-1 whitespace-pre-wrap">{note}</p>}
                    </div>
                  )}
                 </div>
               </div>
             )
           })}
         </div>
      ) : (
        <p className="mt-4 text-sm text-ink/50">
         {action === AdminActivityAction.NominationInfoRequested ? "No information requests recorded." : "No activity recorded."}
       </p>
      )}
      <Modal
        isOpen={selectedCommunication !== null}
        onClose={() => setSelectedCommunication(null)}
        title="Communication"
        subtitle={selectedCommunication ? formatDate(selectedCommunication.createdAt) : undefined}
        maxWidth="lg"
      >
        {selectedCommunication && (() => {
          const metadata = selectedCommunication.metadata ?? {}
          const type = communicationType(selectedCommunication, metadata) ?? "Communication"
          const recipient = metadataText(metadata.sentTo)
          const subject = metadataText(metadata.subject) ?? selectedCommunication.body
          const message = metadataText(metadata.message)

          return (
            <>
              <dl className="grid gap-4 rounded-lg bg-cream-100 p-4 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink/50">Type</dt>
                  <dd className="mt-1 text-ink">{type}</dd>
                </div>
                {recipient && (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-ink/50">To</dt>
                    <dd className="mt-1 break-words text-ink">{recipient}</dd>
                  </div>
                )}
                {subject && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-ink/50">Subject</dt>
                    <dd className="mt-1 break-words text-ink">{subject}</dd>
                  </div>
                )}
              </dl>
              {selectedCommunication.actorName && (
                <p className="mt-4 text-xs text-ink/50">Sent by {selectedCommunication.actorName}</p>
              )}
              {message && (
                <div className="mt-4 rounded-lg border border-ink/10 bg-white p-4 text-sm leading-6 text-ink">
                  <p className="whitespace-pre-wrap break-words">{message}</p>
                </div>
              )}
              <ModalActions>
                <ModalButton onClick={() => setSelectedCommunication(null)}>Close</ModalButton>
              </ModalActions>
            </>
          )
        })()}
      </Modal>
    </div>
  )
}
